import {testIdProps} from '@github-ui/test-id-props'
import {GearIcon, ZapIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Label} from '@primer/react'
import {clsx} from 'clsx'

import type {ClientMemexWorkflow} from '../../api/workflows/contracts'
import {AutoAssignAgentColumnWorkflowDefinition, type ColumnWorkflowState} from '../../helpers/workflow-utilities'
import {WorkflowResources} from '../../strings'
import styles from './column-workflow-indicator.module.css'

type ColumnWorkflowIndicatorProps = {
  columnName: string
  workflowStates: ReadonlyArray<ColumnWorkflowState>
  onConfigureWorkflows: () => void
  onOpenWorkflow: (workflow: ClientMemexWorkflow) => void
}

type ColumnWorkflowStatusLabelProps = {
  workflowStates: ReadonlyArray<ColumnWorkflowState>
}

function columnWorkflowAriaLabel(columnName: string, workflowStates: ReadonlyArray<ColumnWorkflowState>) {
  const firstWorkflowState = workflowStates[0]
  if (workflowStates.length === 1 && firstWorkflowState) {
    if (firstWorkflowState.definition === AutoAssignAgentColumnWorkflowDefinition) {
      return firstWorkflowState.enabled
        ? WorkflowResources.assignCopilotColumnIndicatorEnabled(columnName)
        : WorkflowResources.assignCopilotColumnIndicatorDisabled(columnName)
    }

    return firstWorkflowState.enabled
      ? WorkflowResources.columnWorkflowIndicatorEnabled(columnName, firstWorkflowState.workflow.name)
      : WorkflowResources.columnWorkflowIndicatorDisabled(columnName, firstWorkflowState.workflow.name)
  }

  return WorkflowResources.columnWorkflowsIndicator(columnName, workflowStates.length)
}

function columnWorkflowTestId(workflowStates: ReadonlyArray<ColumnWorkflowState>) {
  return workflowStates.length === 1 && workflowStates[0]?.definition === AutoAssignAgentColumnWorkflowDefinition
    ? 'auto-assign-workflow-column-indicator'
    : 'column-workflow-indicator'
}

export function ColumnWorkflowStatusLabel({workflowStates}: ColumnWorkflowStatusLabelProps) {
  if (workflowStates.length === 0) return null

  const hasEnabledWorkflow = workflowStates.some(workflowState => workflowState.enabled)

  return (
    <Label
      className={clsx(
        styles.ColumnWorkflowStatusLabel,
        !hasEnabledWorkflow && styles.ColumnWorkflowStatusLabelDisabled,
      )}
      {...testIdProps('column-workflow-status-label')}
    >
      {WorkflowResources.assignCopilotColumnStatusLabel}
    </Label>
  )
}

export function ColumnWorkflowIndicator({
  columnName,
  workflowStates,
  onConfigureWorkflows,
  onOpenWorkflow,
}: ColumnWorkflowIndicatorProps) {
  if (workflowStates.length === 0) return null

  const hasEnabledWorkflow = workflowStates.some(workflowState => workflowState.enabled)
  const ariaLabel = columnWorkflowAriaLabel(columnName, workflowStates)

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={ZapIcon}
          variant="invisible"
          size="small"
          aria-label={ariaLabel}
          className={clsx(
            styles.ColumnWorkflowIndicator,
            !hasEnabledWorkflow && styles.ColumnWorkflowIndicatorDisabled,
          )}
          {...testIdProps(columnWorkflowTestId(workflowStates))}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium">
        <ActionList>
          <ActionList.Group>
            <ActionList.GroupHeading>{WorkflowResources.workflowsForColumn(columnName)}</ActionList.GroupHeading>
            {workflowStates.map(workflowState => (
              <ActionList.Item
                key={workflowState.workflow.clientId}
                onSelect={() => onOpenWorkflow(workflowState.workflow)}
                {...testIdProps('column-workflow-indicator-item')}
              >
                <ActionList.LeadingVisual>
                  <span
                    className={clsx(styles.WorkflowStateDot, !workflowState.enabled && styles.WorkflowStateDotDisabled)}
                  />
                </ActionList.LeadingVisual>
                {workflowState.workflow.name}
                <ActionList.Description variant="block">
                  {workflowState.enabled ? WorkflowResources.workflowOn : WorkflowResources.workflowOff}
                </ActionList.Description>
              </ActionList.Item>
            ))}
          </ActionList.Group>
          <ActionList.Divider />
          <ActionList.Item onSelect={onConfigureWorkflows}>
            <ActionList.LeadingVisual>
              <GearIcon />
            </ActionList.LeadingVisual>
            {WorkflowResources.configureWorkflows}
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
